import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback } from "react";
import "./JozwePage.css";

import {
  GRADE_LIST,
  FIELD_LIST,
  getSubjectsFor,
  needsField as curriculumNeedsField,
  normalizeFieldId as curriculumNormalizeField,
  getField as getFieldMeta,
} from "../../../core/education/curriculum.js";

import {
  getJozveChapters,
  buildJozvePdfUrl,
  buildJozveId,
  isLessonPremium,
  sortLessonsByPremium,
  sortChaptersByPremium,
} from "../../../data/jozveData";

import { jozveService } from "../../../services/jozveService";
import { useAuth } from "../../../context/AuthContext";
import { useCoins } from "../../../context/CoinContext";
import JozvePaymentModal from "./JozvePaymentModal";
import { useAdminUploads } from "../../../hooks/useAdminUploads"; // 🆕
import { engine } from "../../../core/books/storage.js";
import { materialHistoryService } from "../../../services/materialHistoryService"; // 🆕 اضافه شد
import { getToken } from "../../../utils/secureStorage"; // 🆕 برای دانلود امن
import { decryptPDF } from "../../../core/workbook/pdfDecryptor"; // 🆕 برای رمزگشایی فایل

// ============================================================
// GRADES و SUBJECTS (همان منطق قبلی)
// ============================================================
const GRADES = GRADE_LIST.map((g) => ({ id: g.id, label: g.title }));

const toSubjectItem = (subject) => ({ id: subject.id, label: subject.title });

const SUBJECTS = (() => {
  const result = {};
  GRADE_LIST.forEach((g) => {
    if (curriculumNeedsField(g.id)) {
      const byField = {};
      FIELD_LIST.forEach((f) => {
        byField[f.id] = getSubjectsFor(g.id, f.id).map(toSubjectItem);
      });
      result[g.id] = byField;
    } else {
      result[g.id] = getSubjectsFor(g.id).map(toSubjectItem);
    }
  });
  return result;
})();

// ============================================================
// دانلود مطمئن PDF (منطق قبلی — دست‌نخورده)
// ============================================================
async function downloadPdfFile(url, filename, materialId) {
  if (!url) return;
  const finalName = filename || url.split("/").pop() || "jozve.pdf";

  try {
    const token = getToken();
    // ۱. دریافت فایل از روت امن با توکن
    const res = await fetch(url, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    let blob = await res.blob();

    // ۲. رمزگشایی فایل (اگر رمزنگاری شده باشد)
    try {
      // 🆕 استفاده از روت امن جدید با توکن JWT و body
      const token = getToken();
      const keyRes = await fetch('/api/secure-jozwe/key', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ bookId: materialId }),
      });
      if (keyRes.ok) {
        const { key, iv } = await keyRes.json();
        const decryptedBuffer = await decryptPDF(await blob.arrayBuffer(), key, iv);
        blob = new Blob([decryptedBuffer], { type: "application/pdf" });
      } else {
        throw new Error('key-fetch-failed');
      }
    } catch (e) {
      console.warn("فایل رمزنگاری نشده بود یا کلید در دسترس نبود. فایل خام دانلود می‌شود.");
      // 🆕 اگه شکست خورد، فقط اگه فایل رمزنگاری‌شده نبوده ادامه بده
      const headBytes = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
      const isPdfMagic = headBytes[0] === 0x25 && headBytes[1] === 0x50 && headBytes[2] === 0x44 && headBytes[3] === 0x46 && headBytes[4] === 0x2D;
      if (!isPdfMagic) {
        throw new Error('فایل رمزگشایی نشد. لطفاً دوباره تلاش کنید.');
      }
    }

    // ۳. دانلود فایل — روش یکسان برای مرورگر و APK
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = finalName;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  } catch (err) {
    console.error("خطا در دانلود امن:", err);
    
    // 🆕 خطای واضح به کاربر
    const errorMsg = err.message?.includes('404') 
      ? 'فایل مورد نظر در سرور یافت نشد. لطفاً با مدیر تماس بگیرید.' 
      : 'خطا در دانلود فایل. لطفاً اتصال اینترنت خود را بررسی کنید.';
    window.alert?.(`❌ ${errorMsg}`);
    
    throw err; // اجازه بده تا لایه بالا هم متوجه شود
  }
}

// ============================================================
// کامپوننت اصلی
// ============================================================
export default function JozwePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();
  const { coins } = useCoins();

  const params = new URLSearchParams(location.search);
  const initialGrade = params.get("grade") || "1";
  const initialField = params.get("field")
    ? curriculumNormalizeField(params.get("field")) || ""
    : "";
  const initialSubject = params.get("subject") || "";

  const [selectedGrade, setSelectedGrade] = useState(initialGrade);
  const [selectedField, setSelectedField] = useState(initialField);
  const [selectedSubject, setSelectedSubject] = useState(initialSubject);
  const [gradeOpen, setGradeOpen] = useState(false);
  const [subjectOpen, setSubjectOpen] = useState(false);
  const [fieldOpen, setFieldOpen] = useState(false);

  // 🆕 وضعیت مودال پرداخت
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    lesson: null,
    meta: null,
  });

  // 🆕 وضعیت مودال تایید دانلود
  const [downloadModal, setDownloadModal] = useState({
    open: false,
    lesson: null,
    jozveId: null,
  });

  // 🆕 تاریخچه کاربر برای نمایش بج‌ها
  const [userHistory, setUserHistory] = useState([]);

  // 🆕 refresh tick برای بج‌های purchased
  const [purchasesTick, setPurchasesTick] = useState(0);
  const allAdminUploads = useAdminUploads();

  // 🆕 نوع محتوا — جزوه یا نمونه سوال
  const [materialType, setMaterialType] = useState(() => {
    const urlType = new URLSearchParams(location.search).get('type');
    if (urlType === 'exam' || urlType === 'jozve') return urlType;
    try { return engine.get('jozwe-last-material-type', 'jozve') || 'jozve'; }
    catch { return 'jozve'; }
  });

  // 🆕 مودال انتخاب اولیه (هر بار ورود)
  const [showTypeModal, setShowTypeModal] = useState(() => {
    const urlType = new URLSearchParams(location.search).get('type');
    if (urlType === 'exam' || urlType === 'jozve') return false;
    return true; // همیشه باز شود
  });

  // 🆕 فیلتر adminUploads بر اساس materialType (بدون re-fetch)
  const adminUploads = useMemo(
    () => allAdminUploads.filter(u =>
      u.category === (materialType === 'jozve' ? 'gama' : 'exam-pdf')
    ),
    [allAdminUploads, materialType]
  );

  const needsField = curriculumNeedsField(selectedGrade);

  // ── لیست دروس قابل نمایش ──
  const currentSubjectsList = useMemo(() => {
    const gradeData = SUBJECTS[selectedGrade];
    if (!gradeData) return [];
    if (Array.isArray(gradeData)) return gradeData;
    if (selectedField && gradeData[selectedField]) {
      return gradeData[selectedField];
    }
    return [];
  }, [selectedGrade, selectedField]);

  // اصلاح خودکار درس
  useEffect(() => {
    if (!currentSubjectsList.length) return;
    const exists = currentSubjectsList.some((s) => s.id === selectedSubject);
    if (!exists) {
      setSelectedSubject(currentSubjectsList[0]?.id || "");
    }
  }, [currentSubjectsList, selectedSubject]);

  // ── فصل‌ها (با مرتب‌سازی premium اول) ──
const chapters = useMemo(() => {
  const deletedKeys = new Set(adminUploads.filter(u => u.isDeleted).map(u => u.bookId));
  const staticPrefix = materialType === 'exam' ? 'static-exam' : 'static-gama';

  const raw = getJozveChapters({
    grade: selectedGrade,
    book: selectedSubject,
    field: needsField ? selectedField : undefined,
    type: materialType,
  });

  // 🆕 فیلتر کردن آیتم‌های استاتیک حذف شده
  const filteredRaw = raw.map(ch => ({
    ...ch,
    lessons: ch.lessons.filter(l => {
      const fieldStr = needsField ? selectedField : 'none';
      const key = `${staticPrefix}-${selectedGrade}-${fieldStr}-${selectedSubject}-${ch.chapterNumber}-${l.lessonNumber}`;
      return !deletedKeys.has(key);
    })
  })).filter(ch => ch.lessons.length > 0);

  const merged = [...filteredRaw];

  // 🆕 تزریق آیتم‌های آپلود شده توسط ادمین
  const filteredCustoms = adminUploads.filter(u => 
    !u.isDeleted && 
    String(u.grade) === String(selectedGrade) && 
    u.subject === selectedSubject && 
    (!needsField || u.field === selectedField)
  );

  const premiumDesc = materialType === 'exam'
    ? 'نمونه سوال ویژه با کیفیت بالا'
    : 'جزوه ویژه با کیفیت بالا';

  filteredCustoms.forEach(u => {
    const chapterNum = Number(u.chapterNumber) || 99;
    const existingChapter = merged.find(c => c.chapterNumber === chapterNum);
    
    const newLesson = {
      lessonNumber: Number(u.lessonNumber) || (existingChapter ? existingChapter.lessons.length + 1 : 1),
      title: u.title,
      file: u.fileUrl,
      isPremium: u.isPremium === true,
      coinPrice: (u.coinPrice !== undefined && u.coinPrice !== null && u.coinPrice !== '') ? Number(u.coinPrice) : 0, 
      description: u.isPremium ? premiumDesc : '',
      badge: u.isPremium ? '⭐ ویژه' : undefined,
      isCustom: true,
      dbId: u.id, 
      fileId: u.bookId || null,  // 🆕 شناسه واقعی فایل برای رمزگشایی
      coverImage: u.coverImage || null,
    };

    if (existingChapter) {
      existingChapter.lessons.push(newLesson);
    } else {
      merged.push({ chapterNumber: chapterNum, lessons: [newLesson] });
    }
  });

// 🆕 مرتب‌سازی عددی محض — فصل‌ها و درس‌ها همیشه به ترتیب شماره
const sorted = [...merged]
  .sort((a, b) => {
    const an = Number(a.chapterNumber) || 0;
    const bn = Number(b.chapterNumber) || 0;
    return an - bn;
  })
  .map((ch) => ({
    ...ch,
    lessons: [...(ch.lessons || [])].sort((a, b) => {
      const an = Number(a.lessonNumber) || 0;
      const bn = Number(b.lessonNumber) || 0;
      return an - bn;
    }),
  }));

return sorted;
}, [selectedGrade, selectedField, selectedSubject, needsField, adminUploads, materialType]);

  // ── Set از jozveId های خریداری‌شده‌ی این کاربر ──
  const [purchasedSet, setPurchasedSet] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.id) {
        setPurchasedSet(new Set());
        return;
      }
      try {
        const list = await jozveService.getUserPurchases(user.id);
        if (cancelled) return;
        setPurchasedSet(new Set((Array.isArray(list) ? list : []).map((p) => p.jozveId)));
      } catch (e) {
        console.warn('[JozwePage] load purchases failed:', e);
        if (!cancelled) setPurchasedSet(new Set());
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.id, purchasesTick]);


  // 🆕 خواندن تاریخچه کاربر از سرور هنگام ورود
  useEffect(() => {
    if (!user?.id) {
      setUserHistory([]);
      return;
    }
    materialHistoryService.getHistory().then(history => {
      setUserHistory(Array.isArray(history) ? history : []);
    });
  }, [user?.id, purchasesTick]);

  const handleDownload = useCallback(
    (lesson) => {
      if (!lesson?.file) return;
      
      const rawUrl = buildJozvePdfUrl({
        grade: selectedGrade,
        book: selectedSubject,
        field: needsField ? selectedField : undefined,
        file: lesson.file,
        type: materialType,
      });

      const secureUrl = `/api/secure-jozwe/stream?file=${encodeURIComponent(rawUrl)}`;
      const filename = lesson.file.split("/").pop() || `jozve-${lesson.lessonNumber}.pdf`;
      
      // 🆕 استفاده از fileId (شناسه واقعی فایل) برای رمزگشایی
      const materialId = lesson.fileId 
        ? lesson.fileId
        : (lesson.dbId 
          ? (materialType === 'exam' ? `custom-exam-${lesson.dbId}` : `custom-${lesson.dbId}`)
          : buildJozveId({
              grade: selectedGrade,
              field: needsField ? selectedField : null,
              book: selectedSubject,
              chapterNumber: lesson.chapterNumber || 1,
              lessonNumber: lesson.lessonNumber,
              type: materialType,
            }));

      downloadPdfFile(secureUrl, filename, materialId);
    },
    [selectedGrade, selectedSubject, selectedField, needsField, materialType]
  );

  // 🆕 تایید نهایی دانلود (بعد از مودال)
  const handleConfirmDownload = useCallback(async () => {
    const { lesson, jozveId } = downloadModal;
    setDownloadModal({ open: false, lesson: null, jozveId: null }); // بستن مودال

    if (!lesson) return;

    try {
      // ۱. ثبت وضعیت "دانلود شده" در دیتابیس سرور
      await materialHistoryService.track(jozveId, 'exam', 'downloaded');
      
      // ۲. آپدیت استیت محلی برای نمایش فوری بج
      setUserHistory(prev => {
        const filtered = prev.filter(h => h.materialId !== jozveId);
        return [...filtered, { materialId: jozveId, status: 'downloaded', type: 'exam' }];
      });

      // ۳. اجرای دانلود واقعی
      handleDownload(lesson);
    } catch (e) {
      console.error('خطا در ثبت تاریخچه دانلود:', e);
      handleDownload(lesson); // حتی اگر ثبت تاریخچه خطا داد، دانلود را متوقف نکن
    }
  }, [downloadModal, handleDownload]);

  // ── کلیک روی کارت: جزوه → ویوئر امن، نمونه سوال → دانلود ──
  const handleLessonClick = useCallback(
    (lesson, chapter) => {
      const isPremium = isLessonPremium(lesson);
      
      // محاسبه jozveId — با پشتیبانی از type
      const jozveId = lesson.dbId
        ? (materialType === 'exam' ? `custom-exam-${lesson.dbId}` : `custom-${lesson.dbId}`)
        : buildJozveId({
            grade: selectedGrade,
            field: needsField ? selectedField : null,
            book: selectedSubject,
            chapterNumber: chapter.chapterNumber,
            lessonNumber: lesson.lessonNumber,
            type: materialType,
          });

      // 🆕 بخش اول: اگر نوع محتوا «جزوه» است → هدایت به ویوئر امن (بدون دانلود)
      if (materialType === 'jozve') {
        if (!isPremium || (user?.id && purchasedSet.has(jozveId))) {
          // 🆕 ذخیره در localStorage برای مقاومت به Kill شدن APK
          try {
            localStorage.setItem(`jozwe-view-${jozveId}`, JSON.stringify({
              fileUrl: lesson.file,
              title: lesson.title,
              decryptId: lesson.fileId || jozveId,
              timestamp: Date.now(),
            }));
          } catch {}

          navigate(`/jozwe-view/${jozveId}`, {
            state: { 
              fileUrl: lesson.file, 
              title: lesson.title,
              decryptId: lesson.fileId || jozveId,
            }
          });
          return;
        }

        if (!user?.id) {
          alert("برای خرید جزوه ویژه ابتدا وارد حساب خود شوید.");
          return;
        }

        // باز کردن مودال پرداخت برای جزوه ویژه
        setPaymentModal({
          open: true,
          lesson,
          meta: {
            jozveId,
            grade: selectedGrade,
            field: needsField ? selectedField : null,
            book: selectedSubject,
            chapterNumber: chapter.chapterNumber,
            lessonNumber: lesson.lessonNumber,
            type: materialType,
          },
        });
        return;
      }

      // 🆕 بخش دوم: اگر نوع محتوا «نمونه سوال» است → باز کردن مودال تایید دانلود
      if (!isPremium) {
        setDownloadModal({ open: true, lesson, jozveId });
        return;
      }

      // اگر قبلاً خریداری شده → باز کردن مودال تایید دانلود
      if (user?.id && purchasedSet.has(jozveId)) {
        setDownloadModal({ open: true, lesson, jozveId });
        return;
      }


      // اگر لاگین نیست → پیام
      if (!user?.id) {
        alert("برای خرید نمونه سوال ویژه ابتدا وارد حساب خود شوید.");
        return;
      }

      // باز کردن مودال پرداخت برای نمونه سوال ویژه
      setPaymentModal({
        open: true,
        lesson,
        meta: {
          jozveId,
          grade: selectedGrade,
          field: needsField ? selectedField : null,
          book: selectedSubject,
          chapterNumber: chapter.chapterNumber,
          lessonNumber: lesson.lessonNumber,
          type: materialType,
        },
      });
    },
    [selectedGrade, selectedSubject, selectedField, needsField, user?.id, purchasedSet, handleDownload, materialType, navigate]
  );

  // ── تأیید پرداخت در مودال ──
  const handlePaymentSuccess = useCallback(async () => {
    const { lesson, meta } = paymentModal;
    if (!lesson || !meta || !user?.id) return;

    try {
      // ۱. ثبت خرید در سرور (منبع حقیقت) — با await
      await jozveService.activatePurchase(user.id, meta.jozveId, {
        grade: meta.grade,
        field: meta.field,
        book: meta.book,
        chapterNumber: meta.chapterNumber,
        lessonNumber: meta.lessonNumber,
        title: lesson.title,
        coinPrice: lesson.coinPrice,
        type: meta.type || materialType,
      });

      // ۲. رفرش بج‌ها
      setPurchasesTick((t) => t + 1);

      // ۳. 🆕 هدایت یا دانلود بر اساس نوع محتوا
      if (materialType === 'jozve') {
        try {
          localStorage.setItem(`jozwe-view-${meta.jozveId}`, JSON.stringify({
            fileUrl: lesson.file,
            title: lesson.title,
            decryptId: lesson.fileId || meta.jozveId,
            timestamp: Date.now(),
          }));
        } catch {}
        
        navigate(`/jozwe-view/${meta.jozveId}`, {
          state: { 
            fileUrl: lesson.file, 
            title: lesson.title,
            decryptId: lesson.fileId || meta.jozveId,
          }
        });
      } else {
        // نمونه سوال ویژه خریداری شد → باز کردن مودال تایید دانلود
        setDownloadModal({ open: true, lesson, jozveId: meta.jozveId });
      }
    } catch (e) {
      console.error("خطا در ثبت خرید:", e);
      throw e;
    }
  }, [paymentModal, user?.id, handleDownload, materialType, navigate]);

  // 🆕 تغییر نوع محتوا — با sync به URL + engine + sessionStorage
  const handleTypeChange = useCallback((nextType) => {
    if (nextType !== 'jozve' && nextType !== 'exam') return;
    setMaterialType(nextType);

    try { engine.set('jozwe-last-material-type', nextType); } catch {}
    

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('type', nextType);
      window.history.replaceState({}, '', url.toString());
    } catch {}

    setShowTypeModal(false);
  }, []);

  // 🆕 بستن مودال بدون انتخاب → پیش‌فرض jozve یا آخرین انتخاب
  const handleTypeModalDismiss = useCallback(() => {
    
    setShowTypeModal(false);
  }, []);

  const handleBack = () => navigate("/books");

  const handleGradeChange = (gradeId) => {
    setSelectedGrade(gradeId);
    setGradeOpen(false);
    if (curriculumNeedsField(gradeId)) {
      setSelectedField("");
      setSelectedSubject("");
    } else {
      setSelectedField("");
      const firstSubject = SUBJECTS[gradeId]?.[0]?.id || "";
      setSelectedSubject(firstSubject);
    }
  };

  const handleFieldChange = (fieldId) => {
    setSelectedField(fieldId);
    setFieldOpen(false);
    const firstSubject = SUBJECTS[selectedGrade]?.[fieldId]?.[0]?.id || "";
    setSelectedSubject(firstSubject);
  };

  const currentGradeLabel =
    GRADES.find((g) => g.id === selectedGrade)?.label || "انتخاب پایه";
  const currentFieldLabel = selectedField
    ? getFieldMeta(selectedField)?.shortTitle || selectedField
    : "";
  const currentSubjectLabel =
    currentSubjectsList.find((s) => s.id === selectedSubject)?.label ||
    "انتخاب درس";

  const isEmpty = chapters.length === 0;
  const emptyMessage =
    needsField && !selectedField
      ? "لطفاً ابتدا رشته تحصیلی را انتخاب کنید"
      : (materialType === 'exam'
          ? "برای این درس هنوز نمونه سوالی ثبت نشده است"
          : "برای این درس هنوز جزوه‌ای ثبت نشده است");

  // ── آمار سریع ──
  const premiumCount = useMemo(
    () =>
      chapters.reduce(
        (acc, ch) => acc + (ch.lessons || []).filter(isLessonPremium).length,
        0
      ),
    [chapters]
  );

  return (
    <div className="jozwe-page">
      {/* پس‌زمینه‌ی aurora */}
      <div className="jozwe-aurora" aria-hidden="true">
        <div className="jozwe-orb orb-a" />
        <div className="jozwe-orb orb-b" />
        <div className="jozwe-orb orb-c" />
      </div>

      {/* ============== هدر ============== */}
      <header className="jozwe-header">
        <div className="jozwe-header-top">
          <button className="jozwe-back-btn" onClick={handleBack}>
            ← بازگشت
          </button>

          <div className="jozwe-coin-chip" title="موجودی سکه شما">
            <span className="jozwe-coin-icon">🪙</span>
            <span className="jozwe-coin-value">
              {coins.toLocaleString("fa-IR")}
            </span>
          </div>
        </div>

        <div className="jozwe-title-block">
          <h1 className="jozwe-title">
            {materialType === 'exam' ? 'نمونه سوالات' : 'جزوه‌های درسی'}
          </h1>
          <p className="jozwe-subtitle">
            {materialType === 'exam' ? 'سوالات امتحانی برای تمرین' : 'درس‌نامه‌های آماده‌ی مطالعه'}
            {premiumCount > 0 && (
              <> — <span className="jozwe-premium-count">
                ⭐ {premiumCount} {materialType === 'exam' ? 'نمونه سوال ویژه' : 'جزوه ویژه'}
              </span></>
            )}
          </p>
        </div>

        {/* ============== Selector pills ============== */}
        <div className="jozwe-selectors">
          <button
            className={`jozwe-pill ${gradeOpen ? "is-open" : ""}`}
            onClick={() => {
              setGradeOpen((p) => !p);
              setFieldOpen(false);
              setSubjectOpen(false);
            }}
          >
            <span className="jozwe-pill-label">پایه</span>
            <span className="jozwe-pill-value">{currentGradeLabel}</span>
            <span className="jozwe-pill-arrow">▾</span>
          </button>

          {needsField && (
            <button
              className={`jozwe-pill ${fieldOpen ? "is-open" : ""}`}
              onClick={() => {
                setFieldOpen((p) => !p);
                setGradeOpen(false);
                setSubjectOpen(false);
              }}
            >
              <span className="jozwe-pill-label">رشته</span>
              <span className="jozwe-pill-value">
                {currentFieldLabel || "انتخاب"}
              </span>
              <span className="jozwe-pill-arrow">▾</span>
            </button>
          )}

          <button
            className={`jozwe-pill is-wide ${subjectOpen ? "is-open" : ""}`}
            onClick={() => {
              if (needsField && !selectedField) {
                setFieldOpen(true);
                return;
              }
              setSubjectOpen((p) => !p);
              setGradeOpen(false);
              setFieldOpen(false);
            }}
          >
            <span className="jozwe-pill-label">درس</span>
            <span className="jozwe-pill-value">{currentSubjectLabel}</span>
            <span className="jozwe-pill-arrow">▾</span>
          </button>
        </div>

        {/* 🆕 کلید دوحالته: جزوه ↔ نمونه سوال */}
        <div className="jozwe-material-switch" role="tablist" aria-label="نوع محتوا">
          <span
            className={`jozwe-material-switch__slider ${materialType === 'exam' ? 'is-right' : 'is-left'}`}
            aria-hidden="true"
          />
          <button
            type="button"
            role="tab"
            aria-selected={materialType === 'jozve'}
            className={`jozwe-material-switch__btn ${materialType === 'jozve' ? 'is-active' : ''}`}
            onClick={() => handleTypeChange('jozve')}
          >
            <span className="jozwe-material-switch__icon">📄</span>
            <span className="jozwe-material-switch__label">جزوه</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={materialType === 'exam'}
            className={`jozwe-material-switch__btn ${materialType === 'exam' ? 'is-active' : ''}`}
            onClick={() => handleTypeChange('exam')}
          >
            <span className="jozwe-material-switch__icon">📝</span>
            <span className="jozwe-material-switch__label">نمونه سوال</span>
          </button>
        </div>

        {/* ============== Dropdowns ============== */}
        {gradeOpen && (
          <div className="jozwe-dropdown">
            {GRADES.map((g) => (
              <div
                key={g.id}
                className={`jozwe-dropdown-item ${
                  selectedGrade === g.id ? "is-active" : ""
                }`}
                onClick={() => handleGradeChange(g.id)}
              >
                {g.label}
              </div>
            ))}
          </div>
        )}

        {fieldOpen && needsField && (
          <div className="jozwe-dropdown">
            {FIELD_LIST.map((f) => (
              <div
                key={f.id}
                className={`jozwe-dropdown-item ${
                  selectedField === f.id ? "is-active" : ""
                }`}
                onClick={() => handleFieldChange(f.id)}
              >
                {f.title}
              </div>
            ))}
          </div>
        )}

        {subjectOpen && currentSubjectsList.length > 0 && (
          <div className="jozwe-dropdown">
            {currentSubjectsList.map((s) => (
              <div
                key={s.id}
                className={`jozwe-dropdown-item ${
                  selectedSubject === s.id ? "is-active" : ""
                }`}
                onClick={() => {
                  setSelectedSubject(s.id);
                  setSubjectOpen(false);
                }}
              >
                {s.label}
              </div>
            ))}
          </div>
        )}
      </header>

      {/* ============== محتوا ============== */}
      <main className="jozwe-content">
        {isEmpty && (
          <div className="jozwe-empty">
            <div className="jozwe-empty-icon">📭</div>
            <p>{emptyMessage}</p>
          </div>
        )}

        {chapters.map((chapter) => {
          const hasPremium = (chapter.lessons || []).some(isLessonPremium);

          return (
            <section
              key={chapter.chapterNumber}
              className={`jozwe-chapter-box ${hasPremium ? "has-premium" : ""}`}
            >
              <div className="jozwe-chapter-label">
                <span className="jozwe-chapter-num">
                  فصل {chapter.chapterNumber}
                </span>
                {hasPremium && (
                  <span className="jozwe-chapter-premium-tag">⭐ ویژه</span>
                )}
              </div>

              <div className="jozwe-lessons-row">
                {(chapter.lessons || []).map((lesson) => {
                  const isPremium = isLessonPremium(lesson);
                  // 🆕 محاسبه jozveId برای همه (رایگان و پریمیوم) جهت نمایش بج
                  const jozveId = lesson.dbId
                    ? (materialType === 'exam' ? `custom-exam-${lesson.dbId}` : `custom-${lesson.dbId}`)
                    : buildJozveId({
                        grade: selectedGrade,
                        field: needsField ? selectedField : null,
                        book: selectedSubject,
                        chapterNumber: chapter.chapterNumber,
                        lessonNumber: lesson.lessonNumber,
                        type: materialType,
                      });
                  const isPurchased = isPremium && purchasedSet.has(jozveId);

                  const cardClass = [
                    "jozwe-lesson-card",
                    isPremium ? "is-premium" : "is-free",
                    isPurchased ? "is-purchased" : "",
                  ].filter(Boolean).join(" ");

                  return (
                    <div
                      key={lesson.dbId ? `custom-${lesson.dbId}` : `static-${chapter.chapterNumber}-${lesson.lessonNumber}`}
                      className={cardClass}
                      onClick={() => handleLessonClick(lesson, chapter)}
                    >
                      {/* 🆕 تصویر کاور جزوه (فقط اگر وجود داشته باشد) */}
                      {lesson.coverImage && (
                        <div className="jozwe-lesson-cover" style={{ width: '100%', height: '120px', borderRadius: '8px', overflow: 'hidden', marginBottom: '8px' }}>
                          <img 
                            src={lesson.coverImage.startsWith('http') 
                              ? lesson.coverImage 
                              : `https://yadoo20.ir${lesson.coverImage}`} 
                            alt={lesson.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }} // اگر عکس لود نشد، مخفی شود
                          />
                        </div>
                      )}

                      {/* بج وضعیت */}
                      {isPremium && (
                        <div className={`jozwe-card-badge ${isPurchased ? "is-owned" : "is-locked"}`}>
                          {isPurchased ? "✓" : "🔒"}
                        </div>
                      )}

                      {/* بج پرفروش/دلخواه */}
                      {isPremium && lesson.badge && !isPurchased && (
                        <div className="jozwe-card-ribbon">{lesson.badge}</div>
                      )}

                      {/* شماره درس / سوال */}
                      <div className="jozwe-lesson-number">
                        {materialType === 'exam' ? 'سوال' : 'درس'} {lesson.lessonNumber}
                      </div>

                      <div className="jozwe-lesson-divider" />

                      <div className="jozwe-lesson-title">{lesson.title}</div>

                      {/* 🆕 بج تاریخچه (مشاهده شده یا دانلود شده) */}
                      {(() => {
                        if (!jozveId) return null;
                        const historyItem = userHistory.find(h => h.materialId === jozveId);
                        if (!historyItem) return null;
                        
                        if (materialType === 'jozve' && historyItem.status === 'viewed') {
                          return <div className="jozwe-history-badge is-viewed">✔️ مشاهده شده</div>;
                        }
                        if (materialType === 'exam' && historyItem.status === 'downloaded') {
                          return <div className="jozwe-history-badge is-downloaded">⬇️ دانلود شده</div>;
                        }
                        return null;
                      })()}

                      {isPremium && lesson.description && (
                        <div className="jozwe-lesson-desc">{lesson.description}</div>
                      )}

                      {/* اکشن */}
                      <div className="jozwe-lesson-action">
                        {isPremium ? (
                          isPurchased ? (
                            <span className="jozwe-action-pill is-download">
                              {materialType === 'jozve' ? '👁️ مشاهده' : '⬇ دانلود'}
                            </span>
                          ) : (
                            <span className="jozwe-action-pill is-buy">
                              🪙 {Number(lesson.coinPrice || 0).toLocaleString("fa-IR")}
                            </span>
                          )
                        ) : (
                          <span className="jozwe-action-pill is-download">
                            {materialType === 'jozve' ? '👁️ مشاهده' : '⬇ دانلود'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </main>

      {/* ============== مودال پرداخت ============== */}
      <JozvePaymentModal
        open={paymentModal.open}
        lesson={paymentModal.lesson}
        onClose={() =>
          setPaymentModal({ open: false, lesson: null, meta: null })
        }
        onSuccess={handlePaymentSuccess}
        itemKind={materialType}
      />

      {/* 🆕 مودال انتخاب نوع محتوا (Bottom Sheet) */}
      {showTypeModal && (
        <div className="jozwe-type-modal-backdrop" onClick={handleTypeModalDismiss}>
          <div className="jozwe-type-modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="jozwe-type-modal-handle" aria-hidden="true" />
            <h3 className="jozwe-type-modal-title">چی می‌خوای ببینی؟</h3>
            <p className="jozwe-type-modal-subtitle">
              یک گزینه انتخاب کن — همیشه می‌تونی از کلید بالای صفحه عوض کنی
            </p>
            <div className="jozwe-type-modal-options">
              <button
                type="button"
                className="jozwe-type-option-card is-jozve"
                onClick={() => handleTypeChange('jozve')}
              >
                <span className="jozwe-type-option-icon">📄</span>
                <strong className="jozwe-type-option-title">جزوه</strong>
                <small className="jozwe-type-option-desc">درس‌نامه و خلاصه</small>
              </button>
              <button
                type="button"
                className="jozwe-type-option-card is-exam"
                onClick={() => handleTypeChange('exam')}
              >
                <span className="jozwe-type-option-icon">📝</span>
                <strong className="jozwe-type-option-title">نمونه سوال</strong>
                <small className="jozwe-type-option-desc">تمرین و آمادگی امتحان</small>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 🆕 مودال تایید دانلود نمونه سوال */}
      {downloadModal.open && (
        <div className="jozwe-download-backdrop" onClick={() => setDownloadModal({ open: false, lesson: null, jozveId: null })}>
          <div className="jozwe-download-modal" onClick={(e) => e.stopPropagation()}>
            <div className="jozwe-download-icon">📥</div>
            <h3 className="jozwe-download-title">دانلود نمونه سوال</h3>
            <p className="jozwe-download-desc">
              آیا می‌خواهید فایل <strong>«{downloadModal.lesson?.title}»</strong> را دانلود کنید؟
            </p>
            <div className="jozwe-download-actions">
              <button 
                className="jozwe-download-btn is-cancel" 
                onClick={() => setDownloadModal({ open: false, lesson: null, jozveId: null })}
              >
                انصراف
              </button>
              <button 
                className="jozwe-download-btn is-confirm" 
                onClick={handleConfirmDownload}
              >
                <span>شروع دانلود</span>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}